import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ConnectivityService } from '@app/core/services/connectivity.service';

export const connectivityInterceptor: HttpInterceptorFn = (
  httpRequest: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const connectivityService = inject(ConnectivityService);

  const attempt = (): Observable<HttpEvent<unknown>> => {
    if (connectivityService.isOffline()) {
      connectivityService.notifyBlockedRequest();
      return connectivityService.whenOnline().pipe(switchMap(attempt));
    }

    return next(httpRequest).pipe(
      tap(() => connectivityService.reportConnectionRestored()),
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 0) {
          return throwError(() => error);
        }

        // A status 0 alone doesn't prove the device is offline - it's the same
        // shape a browser reports for CORS failures, aborted requests, or a TLS/ATS
        // misconfiguration. Only treat it as a connectivity issue if the OS agrees
        // there's no network right now; otherwise let it surface as a real error.
        return from(connectivityService.getConnectivity()).pipe(
          switchMap((connected) => {
            if (connected) {
              return throwError(() => error);
            }

            connectivityService.reportConnectionLost();
            return connectivityService.whenOnline().pipe(switchMap(attempt));
          })
        );
      })
    );
  };

  return attempt();
};
